/**
 * The desktop-registry vocabulary, defined ONCE.
 *
 * Four things have to agree on these strings and none of them can import the others: the registry
 * backend (`plugins/avd-backend`), the scaffolder actions that write `provisioning` /
 * `deprovisioning` at dispatch, the workflow YAML that writes the terminal status at completion,
 * and the My AVD page that shows the caller their desktop's state. A typo in any one of them is a
 * desktop stuck in the wrong state.
 *
 * The My AVD page reads this vocabulary via {@link AVD_DESKTOP_MINE_PATH}: it displays the state and
 * gates which lifecycle actions are live on it.
 *
 * The TypeScript sides are held together by the type checker. The YAML side is held by
 * `plugins/scaffolder-backend-module-avd-provision/src/actions/workflowContract.test.ts`, which owns
 * every workflow↔code join: it asserts that the two lifecycle workflows write the statuses we expect,
 * and the reverse across EVERY workflow that POSTs to the registry — each literal must be a member of
 * {@link AVD_DESKTOP_STATUSES}, and no member may be unwritable. Without that the only guard is the
 * route's runtime 400: one `::warning::` in a nightly run, after which the row silently keeps what it
 * said before.
 */

import type {
	BundleArchiveFacts,
	BundleLastSeenSource,
	BundleLifecycle,
	BundleLifecycleDescriptor,
	BundleVersionFacts,
} from "./bundle";

/**
 * The status of a desktop the registry KNOWS about.
 *
 * - `absent` — the registry has seen this user and they have no desktop (never provisioned, or
 *   deprovisioned successfully). Distinct from `unknown`: this is knowledge, not ignorance.
 * - `provisioning` / `deprovisioning` — a run is in flight. Not terminal; the reconciler settles
 *   these from the GitHub run when a callback is missed.
 * - `ready` — a desktop exists. Says nothing about POWER state; the desktop auto-deallocates
 *   nightly, so `ready` + deallocated is a normal, healthy combination. Power state has its own
 *   endpoint ({@link AVD_DESKTOP_MINE_POWER_PATH}).
 * - `failed` — the last lifecycle run did not succeed. The desktop may or may not exist; the run
 *   link is the truth.
 * - `unconfirmed` — the run finished but we could not verify the outcome. See below: this is NOT a
 *   failure, and saying `failed` here tells a colleague their working desktop is broken.
 */
export type AvdDesktopStatus =
	| "absent"
	| "provisioning"
	| "ready"
	| "failed"
	| "unconfirmed"
	| "deprovisioning";

/** Every member of {@link AvdDesktopStatus}, for runtime validation and the workflow contract test. */
export const AVD_DESKTOP_STATUSES: readonly AvdDesktopStatus[] = [
	"absent",
	"provisioning",
	"ready",
	"failed",
	"unconfirmed",
	"deprovisioning",
];

/*
 * `unconfirmed` — "the desktop was built; our check could not confirm it".
 *
 * WHY THIS STATE EXISTS. A provision can build the desktop perfectly and still have its health gate
 * go blind — e.g. a subscription vended after `azure/login` is invisible to the az CLI (see
 * `.github/scripts/lib/arm.sh`). Writing `failed` then tells a colleague a working desktop did not
 * finish. The gate says *which* of the two it hit, the user-facing notification branches on it, and
 * this status is the registry's half: an unverified outcome gets its own state instead of borrowing
 * `failed`'s.
 *
 * WHAT IT IS NOT:
 *  - not `failed` — nothing suggests the desktop is broken, and the actions it offers are different
 *    (Connect stays live, because trying it is the cheapest way to find out);
 *  - not in flight — no run is running, so {@link isInFlight} is false and the reconciler must leave it
 *    alone. Re-deriving from the run would flip it straight back to `failed`: the run really did
 *    conclude `failure`, which is exactly the fact we are declining to believe about the desktop;
 *  - not `unknown` — {@link AVD_DESKTOP_UNKNOWN} is "we have no row / the registry is down". This is a
 *    row, with a run link, and one specific thing we could not establish.
 *
 * HOW IT RESOLVES. `avd-registry-sync.yaml` (nightly, tenant-level Resource Graph, immune to the trap
 * that causes this state) writes `ready` for every session host that is actually registered, unless the row is in
 * flight or `absent` — so an `unconfirmed` row settles within a day without anyone touching it, and
 * sooner if the colleague re-runs Set up or an operator refreshes the admin inventory.
 */

/**
 * The state of a user the registry has no row for — and the PERMANENT fail-open state.
 *
 * It is returned for a colleague provisioned before the registry existed (until the slice-5
 * backfill runs) and, deliberately, whenever the registry itself is unavailable. The My AVD page
 * renders `unknown` as today's three static cards, so a registry outage degrades the page to the
 * product we shipped rather than to a blank one. This is not transitional scaffolding: it stays.
 */
export const AVD_DESKTOP_UNKNOWN = "unknown";

/** What a caller of `GET /desktops/mine` can see: a known status, or ignorance. */
export type AvdDesktopView = AvdDesktopStatus | typeof AVD_DESKTOP_UNKNOWN;

/** Narrows an arbitrary string to {@link AvdDesktopStatus}. */
export function isAvdDesktopStatus(value: unknown): value is AvdDesktopStatus {
	return typeof value === "string" && (AVD_DESKTOP_STATUSES as readonly string[]).includes(value);
}

/**
 * Statuses with a run still in flight. These are the ones the reconciler re-derives from GitHub,
 * and the ones the My AVD page polls on.
 */
export const AVD_IN_FLIGHT_STATUSES: readonly AvdDesktopStatus[] = [
	"provisioning",
	"deprovisioning",
];

/** True while a lifecycle run is expected to still be running. */
export function isInFlight(status: AvdDesktopStatus): boolean {
	return (AVD_IN_FLIGHT_STATUSES as readonly string[]).includes(status);
}

/** Whether a sweep's write may replace a row in this status; the why is docs/app/avd-registry.md § The nightly sync. */
export function healMayOverwrite(status: AvdDesktopStatus): boolean {
	return !isInFlight(status) && status !== "absent";
}

/**
 * One row of the registry, as the API returns it.
 *
 * Nothing here is sensitive. Slug, UPN, subscription id and VM name are all already printed in
 * plain text in every provisioning run summary; the registry deliberately holds no tfstate, no
 * credentials and no secrets.
 */
export type AvdDesktopRecord = Omit<BundleVersionFacts, "bundleType"> &
	BundleArchiveFacts & {
		/**
		 * Always `avd` on a row read from the registry. Optional here only because the admin inventory also
		 * returns rows it SYNTHESISES for desktops Azure knows and the registry does not (`unregistered`).
		 * Bundle-generic code reads it as `record.bundleType ?? "avd"`.
		 */
		bundleType?: "avd";
		/** Primary key. `toSlug(upn)` — also the user's tfstate key and a component of their resource names. */
		slug: string;
		status: AvdDesktopStatus;
		/** The UPN the slug was derived from. Absent on rows backfilled from tfstate keys alone. */
		upn?: string;
		/** Catalog ref of the owning user, e.g. `user:default/asser`. */
		entityRef?: string;
		/** The vended subscription the desktop lives in. Set when a provision completes. */
		subscriptionId?: string;
		/**
		 * The subscription's display name as ARM reports it (ADR-0022). Absent on rows that never
		 * recorded one; the frontend then derives the legacy name.
		 */
		subscriptionName?: string;
		/** The session host VM. Set when a provision completes; needed for the power-state lookup. */
		vmName?: string;
		/** The Actions run that last moved this desktop, and its link. */
		runId?: number;
		runUrl?: string;
		// bundleType, bundleId, iacVersion, driftStatus, driftCheckedAt: from {@link BundleVersionFacts},
		// the shape every bundle registry shares. A row without iacVersion gains it the next time that
		// desktop is applied.
		/** The golden-image version id the session host was built from. Absent = marketplace image or unknown. */
		imageVersion?: string;
		/** ISO-8601 watermark of the last use (an established connection, or the provision time). Absent = unknown. */
		lastSeenAt?: string;
		lastSeenSource?: BundleLastSeenSource;
		// lifecycle and noticeDeadline: from {@link BundleLifecycleFacts}, written only by the notice routes.
		// Optional because the admin inventory's synthesised rows have no registry row.
		lifecycle?: BundleLifecycle;
		noticeDeadline?: string;
		// holdUntil, holdReason, holdSetBy: from {@link BundleLifecycleFacts}, written only by the hold routes.
		holdUntil?: string;
		holdReason?: string;
		holdSetBy?: string;
		/** ISO-8601. `createdAt` is first sighting; `updatedAt` is the last status write. */
		createdAt: string;
		updatedAt: string;
		/**
		 * What Azure says, merged in by `GET /bundles` from one estate-wide Resource Graph query.
		 * Absent on every other route and whenever no snapshot could be taken — see {@link AvdAzureFacts}.
		 */
		azure?: AvdAzureFacts;
		/** Registry-vs-Azure disagreement, from {@link classifyDrift}. Absent where no snapshot applies. */
		drift?: AvdDesktopDrift;
	};

/** Response body of `GET /desktops/mine`. `status: "unknown"` means no row (see {@link AVD_DESKTOP_UNKNOWN}). */
export type MyAvdDesktopResponse =
	| ({ status: AvdDesktopStatus } & Omit<AvdDesktopRecord, "status">)
	| { status: typeof AVD_DESKTOP_UNKNOWN };

/** Body accepted by `POST /desktops/:slug/status`. Service credentials only. */
export type AvdDesktopStatusUpdate = {
	status: AvdDesktopStatus;
	upn?: string;
	entityRef?: string;
	subscriptionId?: string;
	subscriptionName?: string;
	vmName?: string;
	runId?: number;
	/** `github.run_attempt`: a re-run keeps the run id and raises this. */
	runAttempt?: number;
	runUrl?: string;
	iacVersion?: string;
	/** `null` CLEARS it: the host was rebuilt on the marketplace image or a version-less definition id. */
	imageVersion?: string | null;
	/**
	 * The usage watermark, from the nightly sweep. Forward-only; see {@link resolveLastSeen} for what
	 * each source means on the wire.
	 */
	lastSeenAt?: string;
	lastSeenSource?: BundleLastSeenSource;
	/**
	 * When the current desktop really started (its VM's creation time). Only ever moves the idle floor
	 * EARLIER, so a row stamped at migration time recovers its true age.
	 */
	provisionedAt?: string;
	/**
	 * The nightly sweep's write: refused with a 409 while the row is in flight or `absent`
	 * ({@link healMayOverwrite}), so a lagging Resource Graph cannot undo a lifecycle run's write.
	 */
	heal?: boolean;
	/**
	 * On `failed`: the run failed before it applied anything. A row that was `ready` before the run
	 * goes back to `ready` (docs/app/avd-registry.md § Lifecycle read).
	 */
	nothingApplied?: boolean;
};

/** A watermark ready to persist: `lastSeenAt` absent with source `unknown` records only "no evidence". */
export type AvdResolvedLastSeen = { lastSeenAt?: string; lastSeenSource: BundleLastSeenSource };

/**
 * Turns a sweep's watermark claim into what the registry stores, flooring it at the desktop's
 * provision time so a desktop provisioned yesterday reads as 1 day idle, never as idle since the
 * last connection of a desktop it replaced.
 *
 * - `signal` + `lastSeenAt`: the later of the connection and `provisionedAt`; the source follows the winner.
 * - `created_at`: the sweep saw diagnostics but no connection; the watermark is `provisionedAt`.
 * - `unknown`: no evidence; nothing is claimed.
 *
 * Returns `undefined` when there is nothing to write (no usable timestamp and not `unknown`).
 */
export function resolveLastSeen(
	claim: { lastSeenAt?: string; lastSeenSource?: BundleLastSeenSource },
	provisionedAt: string | undefined,
): AvdResolvedLastSeen | undefined {
	if (claim.lastSeenSource === "unknown") return { lastSeenSource: "unknown" };
	if (claim.lastSeenSource === "created_at") {
		return provisionedAt
			? { lastSeenAt: provisionedAt, lastSeenSource: "created_at" }
			: undefined;
	}
	if (claim.lastSeenSource === "signal" && claim.lastSeenAt) {
		if (provisionedAt && Date.parse(claim.lastSeenAt) < Date.parse(provisionedAt)) {
			return { lastSeenAt: provisionedAt, lastSeenSource: "created_at" };
		}
		return { lastSeenAt: claim.lastSeenAt, lastSeenSource: "signal" };
	}
	return undefined;
}

/**
 * Whole days since the watermark; `undefined` when it is unknown, which is never the same as idle.
 * A `lastSeenSource` of `unknown` means the desktop is blind now, so its old timestamp reads unknown too.
 */
export function daysIdle(
	lastSeenAt: string | undefined,
	now: Date,
	lastSeenSource?: BundleLastSeenSource,
): number | undefined {
	if (!lastSeenAt || lastSeenSource === "unknown") return undefined;
	const ms = now.getTime() - Date.parse(lastSeenAt);
	return Number.isNaN(ms) ? undefined : Math.max(0, Math.floor(ms / 86_400_000));
}

/**
 * Every field of {@link AvdDesktopStatusUpdate}, at runtime — for the workflow contract test, which
 * checks that each key a workflow's jq payload POSTs is one the route reads (a misspelt key is dropped
 * without a word). A `Record` over the type's keys, so a field missing here, or one the type does not
 * have, is a compile error.
 */
const STATUS_UPDATE_FIELDS: Record<keyof AvdDesktopStatusUpdate, true> = {
	status: true,
	upn: true,
	entityRef: true,
	subscriptionId: true,
	subscriptionName: true,
	vmName: true,
	runId: true,
	runAttempt: true,
	runUrl: true,
	iacVersion: true,
	imageVersion: true,
	lastSeenAt: true,
	lastSeenSource: true,
	provisionedAt: true,
	heal: true,
	nothingApplied: true,
};

export const AVD_DESKTOP_STATUS_UPDATE_FIELDS = Object.keys(
	STATUS_UPDATE_FIELDS,
) as readonly (keyof AvdDesktopStatusUpdate)[];

/**
 * The AVD lifecycle descriptor (docs/infra/bundle-contract.md § Lifecycle descriptor). The window and
 * the warning's lead time are set by docs/adr/0024-bundle-inactivity-policy.md.
 */
export const AVD_LIFECYCLE_DESCRIPTOR: BundleLifecycleDescriptor = {
	bundleType: "avd",
	displayName: "desktop",
	ownerPagePath: "/my-avd",
	usageSignal: {
		description: "An established connection to the desktop. A failed attempt is not use.",
		query: 'WVDConnections | where State == "Connected" | summarize lastSeenAt = max(TimeGenerated) by upn = tolower(UserName)',
		bundleKey: { column: "upn", field: "owner.upn" },
		lastSeenColumn: "lastSeenAt",
	},
	inactivityWindowDays: 30,
	warningLeadWorkingDays: 3,
	evaluatedStatuses: ["ready"],
	skipConditions: [
		{
			id: "canary-in-use",
			description:
				"The fresh-vend canary's desktop, which the canary rebuilds on its own schedule.",
			matchAny: [
				{ field: "owner.upn", equalsRepoVariable: "AVD_CANARY_UPN" },
				{ field: "slug", prefix: "canary-" },
			],
		},
	],
	terminalAction: {
		workflow: "avd-deprovision-per-user.yaml",
		inputs: {
			user_slug: "{slug}",
			confirm: "{slug}",
			user_entity_ref: "{entityRef}",
			reclaim_context: "{reclaimContext}",
		},
	},
	enforceSwitch: "INACTIVITY_ENFORCE_AVD",
};

/** Backstage plugin id of the registry backend — the `/api/<id>` segment discovery resolves. */
export const AVD_PLUGIN_ID = "avd";

/**
 * Path of the caller's own-desktop read, relative to the plugin's base URL. Identity-scoped by the
 * backend to the signed-in user — no slug in the path. The My AVD page's only registry read.
 */
export const AVD_DESKTOP_MINE_PATH = "/desktops/mine";

/**
 * Path of the caller's own live power-state read, relative to the plugin's base URL.
 * Identity-scoped exactly like {@link AVD_DESKTOP_MINE_PATH} — no slug in the path.
 *
 * Kept OFF `/desktops/mine` on purpose: this route makes one live ARM call (an `instanceView`), so a
 * slow or 403-ing Azure read must never delay or blank the lifecycle status box. The two answer
 * different questions — `ready` is lifecycle state (does a desktop exist), power state is "is it
 * running right now", and a `ready` desktop is normally powered off between sessions (nightly
 * auto-deallocate).
 */
export const AVD_DESKTOP_MINE_POWER_PATH = "/desktops/mine/power";

/**
 * The live Azure power state of a session-host VM, normalised from Azure's `PowerState/<x>` status
 * code (lower-cased). `unavailable` is the fail-open answer: we could not read it — no Reader grant
 * yet, no managed-identity credential configured, the VM not found, an ARM non-2xx, a timeout, or an
 * unrecognised code (including `PowerState/unknown`). The frontend renders NOTHING for `unavailable`,
 * so the power line simply does not appear and the `ready` box is unchanged.
 */
export type AvdPowerState =
	| "running"
	| "starting"
	| "stopping"
	| "stopped"
	| "deallocating"
	| "deallocated"
	| "unavailable";

/**
 * What Azure itself says about a desktop, read live from Azure Resource Graph (ARG).
 *
 * Kept SEPARATE from {@link AvdDesktopStatus} because the two answer different questions and only
 * one of them is Azure's to answer. The registry owns lifecycle — "a provision run is in flight" is
 * a fact about a GitHub run that Azure cannot observe at all, since a half-built desktop and a
 * deleted one look identical from ARM. Azure owns existence and power. Merging them into one enum
 * would force a lie in whichever direction the merge went.
 */
export type AvdAzureFacts = {
	/** A session host for this slug exists in the estate right now. */
	inAzure: boolean;
	/** Live-ish power state from ARG's `properties.extended.instanceView.powerState.code`. */
	power?: AvdPowerState;
	/** The session host's AVD-side status (`Available`, `Unavailable`, `NeedsAssistance`, …). */
	sessionHostStatus?: string;
};

/**
 * Disagreement between what the registry believes and what Azure has.
 *
 * DISPLAYED, NEVER ACTED ON, because Resource Graph LAGS — a fresh provision or delete takes
 * minutes to surface — so an automatic `ready -> absent` would wipe a healthy row in exactly the
 * window a provision finished. Teardown stays a human decision; this type only makes the
 * disagreement visible.
 *
 * - `none` — the registry and Azure agree.
 * - `missing-in-azure` — the registry says `ready`, ARG has no session host. An orphan CANDIDATE:
 *   could equally be ARG lag, so it is never auto-corrected.
 * - `unregistered` — ARG has a session host the registry has no row for. The nightly sync will
 *   forward-heal this; showing it early means an operator sees it the same day.
 * - `unknown` — NO ARG snapshot was available (no credential, a 403, a timeout). Deliberately
 *   distinct from `none`: "we could not check" must never render as "they agree".
 */
export type AvdDesktopDrift = "none" | "missing-in-azure" | "unregistered" | "unknown";

/** Every member of {@link AvdDesktopDrift}, for runtime validation and exhaustive UI switches. */
export const AVD_DESKTOP_DRIFTS: readonly AvdDesktopDrift[] = [
	"none",
	"missing-in-azure",
	"unregistered",
	"unknown",
];

/**
 * Classifies one row against the estate snapshot. The ONE place drift is decided, so the backend and
 * the admin table can never disagree about what a badge means.
 *
 * `facts` is `undefined` when no snapshot could be taken at all — which is `unknown`, not `none`.
 * In-flight rows are always `none`: a desktop mid-provision is SUPPOSED to be missing from Azure,
 * and flagging it would make every healthy provision look like drift for ten minutes.
 */
export function classifyDrift(
	status: AvdDesktopStatus,
	facts: AvdAzureFacts | undefined,
): AvdDesktopDrift {
	if (!facts) return "unknown";
	if (isInFlight(status)) return "none";
	if (status === "ready" && !facts.inAzure) return "missing-in-azure";
	// `unconfirmed` is deliberately NOT drift, even with no session host in Azure. Drift means
	// the two sides DISAGREE, and an `unconfirmed` row makes no claim to disagree with — the registry
	// is saying it does not know. The estate read still helps here, just not through this function:
	// the row carries its `azure` facts either way, so the Power column is the evidence the run could
	// not get. Calling it drift would re-introduce exactly the false "something is wrong" that the
	// state exists to avoid.
	return "none";
}

/** Response body of `GET /desktops/mine/power`. */
export interface MyAvdPowerResponse {
	power: AvdPowerState;
}

/**
 * The AVD desktop read (the AVD inventory source's `details`) — one entry per desktop the registry knows about, PLUS any
 * session host Azure has that the registry does not (a row synthesised with `status: "absent"` and
 * `drift: "unregistered"`, so the estate is never under-reported).
 */
export type AvdDesktopsResponse = {
	items: AvdDesktopRecord[];
	/**
	 * When the SERVER read the estate, ISO-8601 — not when the browser fetched.
	 *
	 * Client fetch time would overstate freshness: the
	 * reconciler throttles at 60s per row and the ARG snapshot is itself cached, so "just now" could
	 * sit on top of minute-old data. Absent when no snapshot was taken.
	 */
	estateReadAt?: string;
	/**
	 * Whether the Azure snapshot succeeded. `false` means every row's `drift` is `"unknown"` and the
	 * page must SAY it could not check rather than render an empty power column as "powered off".
	 */
	estateAvailable: boolean;
};

/** Path of the service-only status callback, relative to the plugin's base URL. */
export function avdDesktopStatusPath(slug: string): string {
	return `/desktops/${encodeURIComponent(slug)}/status`;
}
